import React, { useState, useEffect } from 'react';
import '../CSS/App.css';
import { NavScroll } from './NavBar';
import SocialLinks from "./SocialLinks";
import { supabase } from '../utils/supabaseClient';

// Import team logos
import BlueLogo from '../assets/BlueLogo.png';
import GoldLogo from '../assets/GoldLogo.png';
import GreenLogo from '../assets/GreenLogo.png';
import PinkLogo from '../assets/PinkLogo.png';

const TEAM_LOGOS = {
  blue: BlueLogo,
  gold: GoldLogo,
  green: GreenLogo,
  pink: PinkLogo
};

const SQUAD_COLORS = {
  blue: '#3b82f6',
  green: '#10b981',
  pink: '#ec4899',
  gold: '#eab308'
};


export default function Squads() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedSquad, setSelectedSquad] = useState('Blue');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setCurrentSlideIndex(0);
  }, [selectedSquad]);

  useEffect(() => {
    // Fetch both squads list and player list from Supabase
    Promise.all([
      supabase
        .from('team_colors')
        .select('team_color')
        .order('team_id', { ascending: true })
        .then(({ data, error }) => {
          if (error) throw error;
          return data.map(r => r.team_color);
        }),
      supabase
        .from('players_joined')
        .select('*')
        .then(({ data, error }) => {
          if (error) throw error;
          return data || [];
        })
    ])
      .then(([teamsData, playersData]) => {
        setTeams(teamsData);
        setPlayers(playersData);
        if (teamsData.length > 0) {
          // Find the first team to match 'Blue' or default to first available
          const hasBlue = teamsData.some(t => t.toLowerCase() === 'blue');
          setSelectedSquad(hasBlue ? 'Blue' : teamsData[0]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to load squads data from Supabase:", err);
        setError("Unable to connect to the Supabase database. Please check configuration.");
        setLoading(false);
      });
  }, []);

  // Filter players by selected official squad color
  const squadPlayers = players.filter(p => 
    p.Team && p.Team.toLowerCase() === selectedSquad.toLowerCase()
  );

  const captains = squadPlayers.filter(p => Number(p.Captain) === 1);
  const regularPlayers = squadPlayers.filter(p => Number(p.Captain) !== 1);
  const sortedSquadPlayers = [...captains, ...regularPlayers];

  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe && currentSlideIndex < sortedSquadPlayers.length - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
    }
  };

  const activeColor = SQUAD_COLORS[selectedSquad.toLowerCase()] || '#2dcfdf';

  return (
    <div className='background-component'>
      <NavScroll />

      <div className="squads-container">
        <h1 className="squads-title">Team Squads</h1>

        {/* Squad Selection Buttons */}
        <div className="squads-selectors">
          {teams.map(name => (
            <button
              key={name}
              className={`squad-button ${name.toLowerCase()} ${selectedSquad === name ? 'active' : ''}`}
              onClick={() => setSelectedSquad(name)}
            >
              {TEAM_LOGOS[name.toLowerCase()] ? (
                <img 
                  src={TEAM_LOGOS[name.toLowerCase()]} 
                  alt={`${name} Squad Selector`} 
                  className="squad-selector-logo" 
                />
              ) : (
                name
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="roster-loading">Loading squad data...</div>
        ) : error ? (
          <div className="roster-error">{error}</div>
        ) : squadPlayers.length === 0 ? (
          <div className="roster-loading" style={{ color: activeColor }}>
            No players found on the {selectedSquad} Squad.
          </div>
        ) : isMobile ? (
          <>
            <div className="squads-carousel-container">
              <button 
                className="carousel-arrow-btn prev" 
                onClick={() => setCurrentSlideIndex(prev => Math.max(0, prev - 1))}
                disabled={currentSlideIndex === 0}
                style={{ borderColor: activeColor, color: activeColor }}
              >
                ‹
              </button>

              <div className="squads-carousel-wrapper">
                <div 
                  className="squads-carousel-track"
                  style={{ transform: `translateX(-${currentSlideIndex * 100}%)` }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {sortedSquadPlayers.map((p, index) => {
                    const isCap = Number(p.Captain) === 1;
                    return (
                      <div className="squads-carousel-slide" key={p.Player || `slide-${index}`}>
                        <div
                          className={`player-card ${isCap ? 'captain' : ''}`}
                          style={{
                            borderColor: activeColor,
                            boxShadow: isCap ? `0 0 25px ${activeColor}70` : `0 0 15px ${activeColor}40`,
                            background: isCap ? `radial-gradient(120% 120% at 50% 10%, ${activeColor}22 0%, rgba(10, 10, 15, 0.85) 75%)` : undefined
                          }}
                        >
                          {isCap ? (
                            <div className="player-card-header-wrapper">
                              <span 
                                className="captain-badge" 
                                style={{ 
                                  color: activeColor,
                                  textShadow: `0 0 10px ${activeColor}`
                                }}
                              >
                                ★ CAPTAIN ★
                              </span>
                              <div className="player-card-header">
                                <h3 className="player-card-name">{p.Player}</h3>
                                <span className="player-card-rank">
                                  {p.Rank ? `#${p.Rank}` : 'Unranked'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="player-card-header">
                              <h3 className="player-card-name">{p.Player}</h3>
                              <span className="player-card-rank">
                                {p.Rank ? `#${p.Rank}` : 'Unranked'}
                              </span>
                            </div>
                          )}
                          
                          <div className="player-card-stats">
                            <div className="player-card-stat">
                              <span className="player-card-label">Events</span>
                              <span className="player-card-value">{p["Events Played"] || 0}</span>
                            </div>
                            <div className="player-card-stat">
                              <span className="player-card-label">Games</span>
                              <span className="player-card-value">{p["Games Played"] || 0}</span>
                            </div>
                            <div className="player-card-stat">
                              <span className="player-card-label">Game Pts</span>
                              <span className="player-card-value">{p["Game Points"] || 0}</span>
                            </div>
                            <div className="player-card-stat">
                              <span className="player-card-label">Bonus Pts</span>
                              <span className="player-card-value">{p["Bonus Points"] || 0}</span>
                            </div>
                            <div className="player-card-stat">
                              <span className="player-card-label">Overall</span>
                              <span className="player-card-value">{p["Overall Points"] || 0}</span>
                            </div>
                            <div className="player-card-stat">
                              <span className="player-card-label">Pts/Game</span>
                              <span className="player-card-value">
                                {typeof p["Pts/Game"] === 'number' ? p["Pts/Game"].toFixed(2) : "0.00"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <button 
                className="carousel-arrow-btn next" 
                onClick={() => setCurrentSlideIndex(prev => Math.min(sortedSquadPlayers.length - 1, prev + 1))}
                disabled={currentSlideIndex === sortedSquadPlayers.length - 1}
                style={{ borderColor: activeColor, color: activeColor }}
              >
                ›
              </button>
            </div>

            <div className="carousel-status-bar">
              <span className="carousel-status-text" style={{ color: activeColor, textShadow: `0 0 8px ${activeColor}` }}>
                PLAYER {currentSlideIndex + 1} OF {sortedSquadPlayers.length}
              </span>
            </div>
          </>
        ) : (
          <div className="squads-content-scrollable">
            {/* Captains Section */}
            {captains.length > 0 && (
              <div className="captains-section">
                <h2 className="section-title" style={{ color: activeColor }}>
                  Captains
                </h2>
                <div className="captains-grid">
                  {captains.map((p, index) => (
                    <div
                      key={p.Player || `cap-${index}`}
                      className="player-card captain"
                      style={{
                        borderColor: activeColor,
                        boxShadow: `0 0 25px ${activeColor}70`,
                        background: `radial-gradient(120% 120% at 50% 10%, ${activeColor}22 0%, rgba(10, 10, 15, 0.85) 75%)`
                      }}
                    >
                      <div className="player-card-header-wrapper">
                        <span 
                          className="captain-badge" 
                          style={{ 
                            color: activeColor,
                            textShadow: `0 0 10px ${activeColor}`
                          }}
                        >
                          ★ CAPTAIN ★
                        </span>
                        <div className="player-card-header">
                          <h3 className="player-card-name">{p.Player}</h3>
                          <span className="player-card-rank">
                            {p.Rank ? `#${p.Rank}` : 'Unranked'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="player-card-stats">
                        <div className="player-card-stat">
                          <span className="player-card-label">Events</span>
                          <span className="player-card-value">{p["Events Played"] || 0}</span>
                        </div>
                        <div className="player-card-stat">
                          <span className="player-card-label">Games</span>
                          <span className="player-card-value">{p["Games Played"] || 0}</span>
                        </div>
                        <div className="player-card-stat">
                          <span className="player-card-label">Game Pts</span>
                          <span className="player-card-value">{p["Game Points"] || 0}</span>
                        </div>
                        <div className="player-card-stat">
                          <span className="player-card-label">Bonus Pts</span>
                          <span className="player-card-value">{p["Bonus Points"] || 0}</span>
                        </div>
                        <div className="player-card-stat">
                          <span className="player-card-label">Overall</span>
                          <span className="player-card-value">{p["Overall Points"] || 0}</span>
                        </div>
                        <div className="player-card-stat">
                          <span className="player-card-label">Pts/Game</span>
                          <span className="player-card-value">
                            {typeof p["Pts/Game"] === 'number' ? p["Pts/Game"].toFixed(2) : "0.00"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Regular Roster Section */}
            <div className="roster-section">
              <h2 className="section-title" style={{ color: activeColor }}>
                Roster
              </h2>
              <div className="regular-players-grid">
                {regularPlayers.map((p, index) => (
                  <div
                    key={p.Player || `reg-${index}`}
                    className="player-card"
                    style={{
                      borderColor: activeColor,
                      boxShadow: `0 0 15px ${activeColor}40`
                    }}
                  >
                    <div className="player-card-header">
                      <h3 className="player-card-name">{p.Player}</h3>
                      <span className="player-card-rank">
                        {p.Rank ? `#${p.Rank}` : 'Unranked'}
                      </span>
                    </div>
                    
                    <div className="player-card-stats">
                      <div className="player-card-stat">
                        <span className="player-card-label">Events</span>
                        <span className="player-card-value">{p["Events Played"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Games</span>
                        <span className="player-card-value">{p["Games Played"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Game Pts</span>
                        <span className="player-card-value">{p["Game Points"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Bonus Pts</span>
                        <span className="player-card-value">{p["Bonus Points"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Overall</span>
                        <span className="player-card-value">{p["Overall Points"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Pts/Game</span>
                        <span className="player-card-value">
                          {typeof p["Pts/Game"] === 'number' ? p["Pts/Game"].toFixed(2) : "0.00"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <SocialLinks />

      <script type="module" src="https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.esm.js"></script>
      <script nomodule src="https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.js"></script>
    </div>
  );
}

